import { Injectable } from '@angular/core';
import { Client, IFrame, IMessage } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { BehaviorSubject, map, Observable, tap } from 'rxjs';
import { Apollo, gql } from 'apollo-angular';
import { environment } from '../../../environments/environment';
import {
  MessageNotification,
  NotificationTask,
} from './message-notification.model';
import { OcrBatchJobDTO } from '../../models/ocr-batch/ocr-batch.model';
import {
  MarkNotifTaskAsRead,
  MarkNotifTaskAsReplied,
  getUnreadNotificationsTask,
  getUnrepliedNotificationsTask,
} from '../../../app/graphql/graphql.queries_notif';


@Injectable({
  providedIn: 'root',
})
export class MessageNotificationService {
  private stompClient: Client | null = null;

  private isConnecting = false;
  private isConnected = false;

  private connected = new BehaviorSubject<boolean>(false);
  public connected$ = this.connected.asObservable();

  private messagesSubject = new BehaviorSubject<MessageNotification[]>([]);
  public messages$ = this.messagesSubject.asObservable();

  private notificationsSubject = new BehaviorSubject<NotificationTask[]>([]);
  public notifications$ = this.notificationsSubject.asObservable();

  public unread$ = this.notifications$.pipe(
    map((list) => list.filter((n) => !n.read)),
  );

  public unreplied$ = this.notifications$.pipe(
    map((list) => list.filter((n) => !n.replied)),
  );

  private unreadCountSubject = new BehaviorSubject<number>(0);
  public unreadCount$ = this.unreadCountSubject.asObservable();

  private unrepliedCountSubject = new BehaviorSubject<number>(0);
  public unrepliedCount$ = this.unrepliedCountSubject.asObservable();
  
  private ocrBatchJobsSubject = new BehaviorSubject<OcrBatchJobDTO[]>([]);
  public ocrBatchJobs$ = this.ocrBatchJobsSubject.asObservable();

  constructor(
    private apollo: Apollo
    
  ) {}

  connect() {
    if (this.isConnecting || this.isConnected) {
      console.log('⚠️ WebSocket already connected or connecting');
      return;
    }

    if (this.stompClient) return;
    this.isConnecting = true;
    const sockJsOptions: any = {
      transports: ['websocket'],
      withCredentials: true,
    };
    const socket = new SockJS(`${environment.apiUrl}/chat`, null, {
      sockJsOptions,
    } as any);

    this.stompClient = new Client({
      webSocketFactory: () => socket as any,
      debug: (str: string) => {
        // Only log in development
        if (!environment.production) {
          console.log('STOMP:', str);
        }
      },
      onConnect: (frame: IFrame) => {
        this.isConnecting = false;
        this.isConnected = true;
        this.connected.next(true);
        console.log('✅ WebSocket connecté');
        this.loadUnreadNotifications();
        this.loadUnrepliedNotifications();
        this.subscribeToQueues();
      },
      onStompError: (frame: IFrame) => {
        console.error('❌ Erreur STOMP:', frame);
        this.isConnecting = false;
        this.isConnected = false;
      },
      onWebSocketClose: (evt: CloseEvent) => {
        console.log('🔌 WebSocket fermée:', evt);
        this.connected.next(false);
        this.isConnecting = false;
        this.isConnected = false;
      },
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
    });

    this.stompClient.activate();
  }
  public isConnectedWebsocket(): boolean {
    return this.isConnected;
  }
  private subscribeToQueues() {
    if (!this.stompClient) return;

    this.stompClient?.subscribe('/user/queue/chat', (message: IMessage) => {
      const msgBody = JSON.parse(message.body);
      this.messagesSubject.next(msgBody);
    });

    this.stompClient.subscribe(
      '/user/queue/notification-message',
      (message: IMessage) => {
        const msgBody = JSON.parse(message.body);
        this.messagesSubject.next(msgBody);
      },
    );

    this.stompClient.subscribe('/user/queue/notifications/new', (msg) => {
      const notification = JSON.parse(msg.body) as NotificationTask;
      const current = this.notificationsSubject.value;
      const filtered = notification.taskId
        ? current.filter((n) => n.taskId !== notification.taskId)
        : current.filter((n) => n.id !== notification.id);
      const updated = [notification, ...filtered];
      this.notificationsSubject.next(updated);
      this.unreadCountSubject.next(updated.filter((n) => !n.read).length);
      this.unrepliedCountSubject.next(updated.filter((n) => !n.replied).length);
    });
    this.stompClient.subscribe('/user/queue/notifications/sync', (msg) => {
      const list: NotificationTask[] = JSON.parse(msg.body) || [];
      // Déduplication par taskId : garder la plus récente par tâche
      const seen = new Map<string, NotificationTask>();
      for (const n of list) {
        const key = n.taskId ? String(n.taskId) : `notask_${n.id}`;
        const existing = seen.get(key);
        if (!existing || Number(n.id) > Number(existing.id)) {
          seen.set(key, n);
        }
      }
      const deduped = Array.from(seen.values());
      this.notificationsSubject.next(deduped);
      this.unreadCountSubject.next(deduped.filter((n) => !n.read).length);
      this.unrepliedCountSubject.next(deduped.filter((n) => !n.replied).length);
    });

    this.stompClient.subscribe(
      '/user/queue/notification-read',
      (message: IMessage) => {
        const unreadCount = parseInt(message.body);

        this.unreadCountSubject.next(unreadCount);
      },
    );

    this.stompClient.subscribe(
      '/user/queue/notification-replied',
      (message: IMessage) => {
        const unrepliedCount = parseInt(message.body);

        this.unrepliedCountSubject.next(unrepliedCount);
      },
    );
    
    this.stompClient.subscribe('/user/queue/ocr-batch/update', (message: IMessage) => {
      const job = JSON.parse(message.body) as OcrBatchJobDTO;
      this.mergeOcrBatchJob(job);
    });

    this.stompClient.subscribe('/user/queue/ocr-batch/item-update', (message: IMessage) => {
      const payload = JSON.parse(message.body) as { jobId: number; item: OcrBatchJobDTO['items'][number] };
      const current = this.ocrBatchJobsSubject.value;
      const job = current.find(j => j.id === payload.jobId);
      if (!job) return;
      const updatedItems = job.items.map(it => it.id === payload.item.id ? payload.item : it);
      this.mergeOcrBatchJob({ ...job, items: updatedItems });
    });
  }

  private mergeOcrBatchJob(job: OcrBatchJobDTO): void {
    const current = this.ocrBatchJobsSubject.value;
    const updated = [job, ...current.filter(j => j.id !== job.id)]
      .sort((a, b) => b.createdAt - a.createdAt)
      .slice(0, 20);
    this.ocrBatchJobsSubject.next(updated);
  }
  
  public setOcrBatchJobsSnapshot(jobs: OcrBatchJobDTO[]): void {
    const current = this.ocrBatchJobsSubject.value;
    const byId = new Map(current.map(j => [j.id, j]));
    jobs.forEach(j => {
      if (!byId.has(j.id)) byId.set(j.id, j);
    });
    const merged = Array.from(byId.values())
      .sort((a, b) => b.createdAt - a.createdAt)
      .slice(0, 20);
    this.ocrBatchJobsSubject.next(merged);
  }

  /** Vide localement l'historique des lots OCR (après un "Clear all" côté serveur). */
  public clearOcrBatchJobs(): void {
    this.ocrBatchJobsSubject.next([]);
  }
  disconnect() {
  if (this.stompClient) {
    this.stompClient.deactivate();
    this.stompClient = null;
  }

  this.isConnected = false;
  this.isConnecting = false;

  this.connected.next(false);

  this.messagesSubject.next([]);
  this.notificationsSubject.next([]);

  this.unreadCountSubject.next(0);
  this.unrepliedCountSubject.next(0);
  this.ocrBatchJobsSubject.next([]);
}

  getMessageNotifications(): Observable<any> {
    return this.apollo.query({
      fetchPolicy: 'network-only',
      query: gql`
        query getMessageNotifications {
          getMessageNotifications {
            senderId
            sender
            requestId
            dealId
            unreadCount
            timestamp
          }
        }
      `,
      variables: {},
    });
  }
  getUnreadNotifications(): Observable<any> {
    return this.apollo.query({
      fetchPolicy: 'network-only',
      query: gql`
        query getMessageNotifications {
          getMessageNotifications {
            senderId
            sender
            requestId
            dealId
            unreadCount
            timestamp
          }
        }
      `,
      variables: {},
    });
  }
  getUnreadNotificationsTask(): Observable<any> {
    return this.apollo.query({
      fetchPolicy: 'no-cache',
      query: getUnreadNotificationsTask,
    });
  }
  getUnrepliedNotificationTasks(): Observable<any> {
    return this.apollo.query({
      fetchPolicy: 'no-cache',
      query: getUnrepliedNotificationsTask,
    });
  }
  markNotificationsAsRead(notificationIds: string[]): Observable<any> {
    return this.apollo.mutate({
      mutation: MarkNotifTaskAsRead,
      variables: {
        notificationIds,
      },
    });
  }
  markAsRead(notificationId: string): Observable<any> {
    return this.markNotificationsAsRead([notificationId]).pipe(
      tap(() => {
        const current = this.notificationsSubject.value;

        const updated = current.filter((n) => n.id !== notificationId);
        this.notificationsSubject.next(updated);
        this.unreadCountSubject.next(updated.filter((n) => !n.read).length);
      }),
    );
  }
  markNotificationsAsReplied(notificationIds: string[]): Observable<any> {
    return this.apollo.mutate({
      mutation: MarkNotifTaskAsReplied,
      variables: {
        notificationIds,
      },
    });
  }
  markAsReplied(notificationId: string): Observable<any> {
    return this.markNotificationsAsReplied([notificationId]).pipe(
      tap(() => {
        const current = this.notificationsSubject.value;

        const updated = current.filter((n) => n.id !== notificationId);
        this.notificationsSubject.next(updated);
        this.unrepliedCountSubject.next(updated.length);
      }),
    );
  }
  private mergeNotifications(newNotifs: NotificationTask[]) {
    const current = this.notificationsSubject.value || [];
    const allNotifs = [
      ...current,
      ...newNotifs.filter((n) => !current.some((c) => c.id === n.id)),
    ];
    // Déduplication par taskId : garder uniquement la notification la plus récente (id le plus grand) par tâche
    const seen = new Map<string, NotificationTask>();
    for (const n of allNotifs) {
      const key = n.taskId ? String(n.taskId) : `notask_${n.id}`;
      const existing = seen.get(key);
      if (!existing || Number(n.id) > Number(existing.id)) {
        seen.set(key, n);
      }
    }
    const deduped = Array.from(seen.values());
    this.notificationsSubject.next(deduped);
    this.unreadCountSubject.next(deduped.filter((n) => !n.read).length);
    this.unrepliedCountSubject.next(deduped.filter((n) => !n.replied).length);
  }
  loadUnreadNotifications() {
    this.getUnreadNotificationsTask().subscribe({
      next: (result) => {
        const notifications = result.data?.unreadNotifications || [];
        this.mergeNotifications(notifications);
      },
      error: (err) => {
        console.error('Error loading unread notifications', err);
      },
    });
  }

  loadUnrepliedNotifications() {
    this.getUnrepliedNotificationTasks().subscribe({
      next: (result) => {
        const notifications = result.data?.unrepliedNotifications || [];
        this.mergeNotifications(notifications);
      },
      error: (err) => {
        console.error('Error loading unreplied notifications', err);
      },
    });
  }
}
